import { Info } from "@phosphor-icons/react";
import clsx from "clsx";

import Skeleton from "common/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { ALL_METRICS } from "common/constants";
import { typedObjectEntries } from "common/helpers/utils";
import type { Metrics } from "common/types";

interface MetricSelectorProps {
  metricsSum: Metrics;
  selectedMetric: keyof Metrics;
  setSelectedMetric: (metric: keyof Metrics) => void;
  isLoading: boolean;
  hiddenColumns?: Array<keyof Metrics>;
  totalMetrics?: Metrics;
}

export default function MetricSelector({
  metricsSum,
  setSelectedMetric,
  selectedMetric,
  isLoading,
  hiddenColumns = [],
  totalMetrics = null,
}: MetricSelectorProps) {
  const filteredMetrics = typedObjectEntries(ALL_METRICS).filter(
    ([key]) => !hiddenColumns.includes(key),
  );

  return (
    <div className="flex overflow-x-auto scrollbar-thin max-lg:gap-x-2 shrink-0">
      {filteredMetrics.map(
        ([
          key,
          { label, icon: Icon, tooltipText, selectedClassName, className },
        ]) => {
          const isSelected = selectedMetric === key;

          return (
            <button
              key={key}
              type="button"
              className={clsx(
                "flex shrink-0 grow basis-44 flex-col items-stretch gap-y-0.5 border border-black-200 px-3 py-2.5 transition-colors max-lg:rounded-2xl lg:gap-y-1 lg:border-l-0 lg:py-3 lg:first:rounded-l-xl lg:first:border-l lg:last:rounded-r-xl",
                isSelected ? selectedClassName.button : className.hover,
              )}
              onClick={() => setSelectedMetric(key)}
            >
              <div className="flex items-center gap-x-1">
                <Icon className={clsx("size-4 lg:size-5", className.icon)} />
                <span className="text-nowrap text-xs">{label}</span>
                <Tooltip>
                  <TooltipTrigger className="max-lg:hidden" asChild>
                    {/** biome-ignore lint/a11y/useSemanticElements: component parent is a button, this component can't be a button */}
                    <div tabIndex={0} role="button" aria-label="more info">
                      <Info
                        weight="fill"
                        className={clsx(
                          isSelected ? "fill-black-700" : "fill-black-300",
                          "size-5 transition-colors",
                        )}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{tooltipText}</TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-end">
                {isLoading ? (
                  <Skeleton className="mr-auto h-7 w-16 rounded-lg" />
                ) : (
                  <span className="text-lg-bold lg:text-xl-bold">
                    + {metricsSum[key]}
                  </span>
                )}
                {totalMetrics && (
                  <span className="ml-auto text-xs">
                    total {totalMetrics[key]}
                  </span>
                )}
              </div>
            </button>
          );
        },
      )}
    </div>
  );
}
